import { DROP_ROOT, DndDivider, DndMode, useDndStore } from "designSystem/dnd/index";
import { FlexColumn } from "designSystem/index";
import { compact } from "lodash";
import { observer } from "mobx-react-lite";
import React, { FC, PropsWithChildren, ReactElement, useEffect, useRef } from "react";

interface ColumnDropDividerWrapperProps extends PropsWithChildren {
	/** placeholder for dnd item while dragging */
	dropPlaceholder?: ReactElement;
	/** gap between items */
	gap?: number;
	/** hide the item being dragged from display */
	hideDraggedItem?: boolean;
	/** dnd mode */
	mode?: DndMode;
	/** parentId */
	parentId?: string;
}

export const ColumnDropDividerWrapper: FC<ColumnDropDividerWrapperProps> = observer(({
	children,
	dropPlaceholder,
	gap = 0,
	hideDraggedItem,
	mode = DndMode.Divider,
	parentId = DROP_ROOT,
}) => {
	const {
		dragItem,
		selectedDivider,
		setDividerRefs,
		isDragging,
	} = useDndStore();
	const ref = useRef<HTMLDivElement>(null);
	const dividerRefs = useRef<Array<HTMLDivElement | null>>([]);
	let childrenWithDividers: React.JSX.Element[];

	// set divider refs on mount
	useEffect(() => {
		setDividerRefs(parentId);
		if (!hideDraggedItem || (isDragging && hideDraggedItem && dragItem)) {
			setDividerRefs(parentId, dividerRefs);
		}
	}, [isDragging, hideDraggedItem && dragItem]);

	if (mode === DndMode.Divider) {
		// this is the default mode, which adds dnd dividers to the top and bottom of each list item
		const childrenArray = React.Children.toArray(children);

		// don't continue if the length is 0
		if ((hideDraggedItem && (!isDragging || !dragItem))) {
			return children;
		}
	
		const showThisGroup = selectedDivider?.parentId === parentId;
		const showIndex = selectedDivider?.index - 1;

		function createDndDivider(index: number) {
			const show = showThisGroup && showIndex === index - 1;
			return (
				dropPlaceholder ? (
					<div
						key={`csi-react-dnd-dropPlaceholder-divider-${parentId}-${index}`}
						ref={(el) => {
							dividerRefs.current[index] = el;
							dividerRefs.current = compact(dividerRefs.current);
						}}
						style={show ? {} : { visibility: "hidden", height: 1 }}
					>
						{dropPlaceholder}
					</div>
				) : (
					<DndDivider
						key={`csi-react-dnd-divider-${parentId}-${index}`}
						ref={(el) => {
							dividerRefs.current[index] = el;
							dividerRefs.current = compact(dividerRefs.current);
						}}
						show={show}
					/>
				)
			);
		};

		childrenWithDividers = childrenArray.reduce((acc: JSX.Element[], child: JSX.Element, index: number) => {
			if (!(hideDraggedItem && dragItem && child.props.dragItem === dragItem && isDragging)) {
				acc.push(child, createDndDivider((acc.length / 2) + 1));
			}
			return acc;
		}, []) as JSX.Element[];
	
		// add a divider at the start of the array
		childrenWithDividers.unshift(createDndDivider(0));
	} else if (dropPlaceholder) {
		const childrenArray = React.Children.toArray(children);

		// don't continue if the length is 0
		if (childrenArray.length === 0) {
			return children;
		}

		childrenWithDividers = childrenArray.map((child: JSX.Element, index: number) => {
			const showPlaceholder = selectedDivider && selectedDivider.parentId === parentId && selectedDivider.index === index;
			return (
				<div
					key={`csi-react-dnd-child-dropPlaceholder-normal${parentId}-${index + 1}`}
					ref={(el) => {
						dividerRefs.current[index] = el;
						dividerRefs.current = compact(dividerRefs.current);
					}}
				>
					{showPlaceholder ? dropPlaceholder : child}
				</div>
			);
		});
	} else {
		// DndMode is normal, so hide the visibility of the item being dragged
		const childrenArray = React.Children.toArray(children);

		// don't continue if the length is 0
		if (childrenArray.length === 0) {
			return children;
		}

		const doNotShowGroup = selectedDivider && selectedDivider.parentId !== parentId;

		childrenWithDividers = childrenArray.flatMap((child: JSX.Element, index: number) => [
			<div
				key={`csi-react-dnd-child-normal-${parentId}-${index + 1}`}
				ref={(el) => {
					dividerRefs.current[index] = el;
					dividerRefs.current = compact(dividerRefs.current);
				}}
				style={{ visibility: doNotShowGroup || (selectedDivider && selectedDivider.index === index) ? "hidden" : "unset" }}
			>
				{child}
			</div>,
		]);
	}

	return (
		<FlexColumn
			gap={gap}
			ref={ref}
		>
			{childrenWithDividers}
		</FlexColumn>
	);
});
